/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file LinkedinComposeSheet.js
 * @description
 * The LinkedIn counterpart of the e-mail composer: a modal sheet to write a direct message (or, for a
 * non-connection, a connection-invite note) to a lead / customer / contact through the user's own
 * connected LinkedIn account. E-mail templates are reusable — the user's mail templates (the same
 * `{{name}}`, `{{firstName}}`, `{{company}}` variables) are listed as chips, rendered to plain text by the
 * backend (`POST /linkedin/templates/render`, with a local fallback) and dropped into the editor.
 *
 * The backend mirrors the sent message on the entity's activity feed and logs it on leads, so follow-ups
 * and notifications keep working exactly as for e-mail.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, Modal, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';

import HappyAlert from '../HappyAlert';
import Icon from '../management/SafeIcon';
import {PrimaryButton} from '../management/ManagementUI';
import {useManagementTheme} from '../../helpers/managementTheme';
import {getMailSettings} from '../../services/mailApi';
import linkedinApi, {isNotConnectedError} from '../../services/linkedinApi';
import {invalidateLinkedinStatus} from '../../hooks/useLinkedinStatus';
import {
    LINKEDIN_INVITE_NOTE_LIMIT,
    LINKEDIN_MESSAGE_LIMIT,
    buildTemplateVariables,
    templateToLinkedinText,
} from '../../helpers/linkedinUtils';

/** LinkedIn brand blue — used on the glyph only (the app brand stays orange). */
export const LINKEDIN_BLUE = '#0A66C2';

/**
 * @typedef {Object} LinkedinRecipient
 * @property {string} [name]
 * @property {string} [company]
 * @property {string} [email]
 * @property {string} [phone]
 * @property {string} [position]
 * @property {string} [linkedinUrl] Profile URL (required unless providerId is given).
 * @property {string} [providerId] LinkedIn provider id (search hits / conversation participants).
 * @property {('lead'|'customer'|'contact')} [entityType] CRM record the message is logged on.
 * @property {string} [entityId]
 * @property {string} [followUpId] Open sales follow-up that this send completes.
 */

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {LinkedinRecipient|null} props.recipient
 * @param {() => void} props.onClose
 * @param {(result: object) => void} [props.onSent] Called with the backend send result.
 * @returns {JSX.Element|null}
 */
const LinkedinComposeSheet = ({visible, recipient, onClose, onSent}) => {
    const m = useManagementTheme();
    const {t} = useTranslation();
    const navigation = useNavigation();
    const [text, setText] = useState('');
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [busy, setBusy] = useState(false);
    const [rendering, setRendering] = useState(false);

    // A fresh recipient starts a fresh message.
    useEffect(() => {
        if (visible) {
            setText('');
            setTemplateId('');
        }
    }, [visible, recipient?.linkedinUrl, recipient?.entityId]);

    // The user's e-mail templates (own + shared) double as LinkedIn templates.
    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        let alive = true;

        getMailSettings()
            .then((settings) => {
                if (alive) {
                    setTemplates(Array.isArray(settings?.templates?.items) ? settings.templates.items : []);
                }
            })
            .catch(() => alive && setTemplates([]));

        return () => {
            alive = false;
        };
    }, [visible]);

    const variables = useMemo(() => buildTemplateVariables(recipient || {}), [recipient]);
    const limit = LINKEDIN_MESSAGE_LIMIT;
    const trimmed = text.trim();
    const canSend = Boolean(trimmed) && trimmed.length <= limit && !busy;

    /** Insert a template: server-side render first (knows the entity), local fill as the fallback. */
    const applyTemplate = useCallback(async (tpl) => {
        setTemplateId(tpl.id);
        setRendering(true);

        try {
            const rendered = await linkedinApi.renderTemplate({
                templateId: tpl.id,
                ...(recipient?.entityType && recipient?.entityId
                    ? {entityType: recipient.entityType, entityId: recipient.entityId}
                    : {variables}),
            });

            setText(rendered?.text || templateToLinkedinText(tpl, variables));
        } catch (_e) {
            setText(templateToLinkedinText(tpl, variables));
        } finally {
            setRendering(false);
        }
    }, [recipient, variables]);

    const handleSend = useCallback(async () => {
        if (!canSend || !recipient) {
            return;
        }

        setBusy(true);

        try {
            const result = await linkedinApi.send({
                text: trimmed,
                ...(recipient.entityType && recipient.entityId
                    ? {entityType: recipient.entityType, entityId: recipient.entityId}
                    : {}),
                ...(recipient.linkedinUrl ? {linkedinUrl: recipient.linkedinUrl} : {}),
                ...(recipient.providerId ? {providerId: recipient.providerId} : {}),
                ...(templateId ? {templateId} : {}),
                ...(recipient.followUpId ? {followUpId: recipient.followUpId} : {}),
            });

            if (result?.status === 'failed') {
                HappyAlert.error(t('linkedin.send_failed_title', 'LinkedIn üzenet'), result.error || t('linkedin.send_failed', 'Az üzenet nem küldhető el.'));

                return;
            }

            Toast.show({
                type: 'success',
                text1: result?.status === 'invite_sent'
                    ? t('linkedin.invite_sent', 'Meghívó elküldve a megjegyzéssel')
                    : t('linkedin.sent', 'LinkedIn üzenet elküldve'),
            });

            if (onSent) {
                onSent(result);
            }

            onClose();
        } catch (error) {
            if (isNotConnectedError(error) || error?.code === 'LINKEDIN_NEEDS_RECONNECT') {
                invalidateLinkedinStatus();
                HappyAlert.info(
                    t('linkedin.not_connected_title', 'LinkedIn nincs csatlakoztatva'),
                    t('linkedin.not_connected_message', 'Csatlakoztassa a LinkedIn fiókját a beállításokban.'),
                );
                onClose();
                navigation.navigate('LinkedinConnectScreen');

                return;
            }

            if (error?.status === 429) {
                HappyAlert.info(t('linkedin.rate_limited_title', 'LinkedIn limit'), t('linkedin.rate_limited', 'A LinkedIn napi limitje elérve, próbálja később.'));

                return;
            }

            HappyAlert.error(t('linkedin.send_failed_title', 'LinkedIn üzenet'), error?.message || t('linkedin.send_failed', 'Az üzenet nem küldhető el.'));
        } finally {
            setBusy(false);
        }
    }, [canSend, navigation, onClose, onSent, recipient, t, templateId, trimmed]);

    if (!visible || !recipient) {
        return null;
    }

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.scrim}>
                <View style={[styles.card, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(3)]}>
                    <View style={[styles.header, {borderBottomColor: m.colors.borderSoft}]}>
                        <Icon name="linkedin" size={22} color={LINKEDIN_BLUE} style={{marginRight: 9}} />
                        <View style={{flex: 1}}>
                            <Text style={[styles.title, {color: m.colors.heading}]} numberOfLines={1}>
                                {t('linkedin.compose_title', 'LinkedIn üzenet')}
                            </Text>
                            <Text style={[styles.subtitle, {color: m.colors.subText}]} numberOfLines={1}>
                                {[recipient.name, recipient.company].filter(Boolean).join(' · ') || recipient.linkedinUrl}
                            </Text>
                        </View>
                        <TouchableOpacity onPress={onClose} hitSlop={10} style={[styles.close, {backgroundColor: m.colors.surfaceAlt}]}>
                            <Icon name="close" size={18} color={m.colors.heading} />
                        </TouchableOpacity>
                    </View>

                    <ScrollView style={styles.body} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                        {templates.length > 0 && (
                            <View style={{marginBottom: 10}}>
                                <Text style={[styles.label, {color: m.colors.subText}]}>{t('linkedin.templates', 'Sablonok')}</Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                                    {templates.map((tpl) => {
                                        const active = tpl.id === templateId;

                                        return (
                                            <TouchableOpacity
                                                key={tpl.id}
                                                onPress={() => applyTemplate(tpl)}
                                                style={[styles.chip, {
                                                    backgroundColor: active ? m.colors.primary : m.colors.surfaceAlt,
                                                    borderColor: active ? m.colors.primary : m.colors.border,
                                                }]}
                                            >
                                                <Text style={[styles.chipText, {color: active ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>
                                                    {tpl.name || t('linkedin.template_untitled', 'Névtelen sablon')}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </ScrollView>
                            </View>
                        )}

                        <View>
                            <TextInput
                                value={text}
                                onChangeText={setText}
                                multiline
                                placeholder={t('linkedin.compose_placeholder', 'Írja meg az üzenetet…')}
                                placeholderTextColor={m.colors.muted}
                                style={[styles.input, {color: m.colors.text, borderColor: m.colors.border, backgroundColor: m.colors.surfaceAlt}]}
                                maxLength={limit + 200}
                                textAlignVertical="top"
                            />
                            {rendering && <ActivityIndicator style={styles.rendering} color={m.colors.primary} />}
                        </View>

                        <Text style={[styles.hint, {color: trimmed.length > limit ? m.colors.danger : m.colors.subText}]}>
                            {`${trimmed.length}/${limit}`}
                            {'  ·  '}
                            {t('linkedin.invite_hint', {defaultValue: 'Nem kapcsolatnak a szöveg a meghívó {{limit}} karakteres megjegyzése lesz.', limit: LINKEDIN_INVITE_NOTE_LIMIT})}
                        </Text>
                    </ScrollView>

                    <View style={[styles.footer, {borderTopColor: m.colors.borderSoft}]}>
                        <PrimaryButton
                            label={t('linkedin.send', 'Küldés LinkedInen')}
                            icon="send"
                            onPress={handleSend}
                            loading={busy}
                            disabled={!canSend}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    scrim: {flex: 1, backgroundColor: 'rgba(15,23,42,0.45)', alignItems: 'center', justifyContent: 'center', padding: 18},
    card: {width: '100%', maxWidth: 520, maxHeight: '86%', borderRadius: 24, borderWidth: 1, overflow: 'hidden'},
    header: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 16, borderBottomWidth: 1},
    title: {fontSize: 16, fontWeight: '900'},
    subtitle: {fontSize: 12, marginTop: 2},
    close: {width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center'},
    body: {paddingHorizontal: 18, paddingTop: 12},
    label: {fontSize: 12, fontWeight: '700', marginBottom: 8},
    chip: {borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7, marginRight: 8, maxWidth: 200},
    chipText: {fontSize: 12, fontWeight: '700'},
    input: {minHeight: 150, borderWidth: 1, borderRadius: 16, padding: 12, fontSize: 14},
    rendering: {position: 'absolute', right: 12, top: 12},
    hint: {fontSize: 11, marginTop: 8, marginBottom: 12},
    footer: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1},
});

export default LinkedinComposeSheet;
